import { transform } from 'ol/proj';
import { config } from '../../config/Config';
import { createLayer } from './MapUtils';
import GeoJSON from 'ol/format/GeoJSON';
import { getTranslatedKey } from './TranslateUtils';
import { getBottomLeft, getTopRight } from 'ol/extent';
import { toast } from 'react-toastify';

export const visorVariables = {
  name: 'Hipermapa',
  descripcio: getTranslatedKey('sidebarText'),
  correuSuggeriment:
    'https://ovt.gencat.cat/gsitfc/AppJava/generic/conqxsGeneric.do?webFormId=391&reqCode=newCqs&topicLevel1.id=10755&topicLevel2.id=10782',
  urlManual: 'https://territori.gencat.cat/web/.content/home/serveis/visors-cartografia/hipermapa/hipermapa-guia-us.pdf',
  urlTutorial: 'https://territori.gencat.cat/ca/serveis/visors-cartografia/hipermapa/videos-ajuda/',
  urlFaqs: 'https://territori.gencat.cat/ca/serveis/visors-cartografia/hipermapa/preguntes-mes-frequents-hipermapa/'
};

export const displayComponents = {
  //navbar
  navbar: true,
  sidebar: true,
  cerca: true,
  cercaAvançada: true,
  descarrega: true,
  imprimir: true,
  compartir: true,
  accessibilitat: true,
  contact: true,
  ajuda: true,
  fullScreen: true,

  //Ajuda
  tutorial: true,
  faqs: false,
  manual: true,

  //Cerca
  cercaLayers: true,
  cercaFeatures: true,
  cercaPlaces: true,
  infoCerca: true,

  //Gestió de mapes
  meusMapes: true,
  mapesPredefinits: true,
  fitxerLocal: true,

  //sidebar
  infoA11y: true,
  infoCookies: true,

  //botons sobre mapa
  selectorFons: true,
  einesMesura: true,
  llegenda: true,
  cataleg: true,
  ubicacio: true,

  //llegenda
  llegendaClear: true,
  llegendaExpand: true,
  llegendaHide: true,

  //Eines de capa
  hideCapa: true,
  transCapa: true,
  infoCapa: true,
  metadadesCapa: true,
  styleCapa: true,
  llegendaFiltre: true,
  taulaDadesCapa: true,
  statsCapa: true,
  exportarCapa: true,
  zoomCapa: true,
  eliminarCapa: true,

  //Catàleg
  catalegExterns: true,
  catalegUsuari: true,
  catalegCorp: true,

  //Info punt
  infoPunt: true,
  infoPuntInfo: true,
  infoPuntPDF: true,
  featureExport: true,

  //Redlining
  drawDibuix: true,
  drawPunt: true,
  drawLinia: true,
  drawArea: true,
  drawCercle: true,
  drawLliure: true,
  drawText: true,
  drawDonwload: true,

  //funcionalitats
  escala: true,
  edicio: true,
  mousePos: true,
  galeria: true,
  preferits: true
};

/**
 * @desc Función que carga las propiedades del mapa del visor como la BBOX, transparencia de fondo y lista de capas.
 */
export const getMapProperties = async (xml, idCat) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml, 'text/xml');
  const generalElement = xmlDoc?.querySelector('General');
  const extensionElement = generalElement?.querySelector('Extension');
  const capaFons = extensionElement?.querySelector('CapaFons')?.textContent;
  const transparenciaFons = extensionElement?.querySelector('TransparenciaFons')?.textContent;
  const layerListNode = xmlDoc?.getElementsByTagName('LayerList')[0];
  const layerNodes = layerListNode?.getElementsByTagName('Layer');
  const boundingBoxNode = xmlDoc?.querySelector('BoundingBox');
  const mapName = xmlDoc?.querySelector('Abstract').textContent;
  const projection = boundingBoxNode?.getAttribute('SRS');
  let redLining = extensionElement?.querySelector('Redlining')?.textContent;
  if (redLining) {
    redLining = JSON.parse(redLining);
  }
  const favsNodes = extensionElement?.getElementsByTagName('Favorite');

  let favs = Array.from(favsNodes).map((fav) => {
    let capa = fav.getAttribute('capa');
    let id = fav.getAttribute('fid');
    return { capa: capa, fid: id };
  });
  const { minx, miny, maxx, maxy } = boundingBoxNode.attributes;
  let bbox = [Number(minx?.value), Number(miny?.value), Number(maxx?.value), Number(maxy?.value)];
  let diagonal = [getBottomLeft(bbox), getTopRight(bbox)];
  diagonal[0] = transform(diagonal[0], projection, config.EPSG_mapa).map(Math.ceil);
  diagonal[1] = transform(diagonal[1], projection, config.EPSG_mapa).map(Math.floor);
  bbox = diagonal.reduce((acc, curr) => acc.concat(curr), []);
  let layers = Array.from(layerNodes).map((layer) => {
    const name = layer?.getElementsByTagName('Name')[0]?.textContent;
    const transparent = Number(layer?.getAttribute('transparent'));
    const visible = Number(layer?.getAttribute('hidden')) === 0 ? true : false;
    const infoEnabled = Number(layer?.getAttribute('queried')) === 0 ? false : true;
    const stylelistXml = layer?.getElementsByTagName('StyleList')[0];
    const styleXml = stylelistXml?.getElementsByTagName('Style')[0];
    const styleDescriptor = styleXml?.getElementsByTagName('StyledLayerDescriptor')[0];
    const styleNamed = styleDescriptor?.getElementsByTagName('NamedLayer')[0];
    const style = styleNamed?.getElementsByTagName('Name')[0]?.textContent;
    const extension = layer?.getElementsByTagName('Extension')[0];
    const indexCapa = Number(extension?.getElementsByTagName('IndexCapa')[0]?.textContent);

    return {
      name,
      transparent,
      style,
      visible,
      indexCapa,
      infoEnabled
    };
  });
  return {
    idCat,
    capaFons,
    transparenciaFons,
    layers,
    bbox,
    redLining,
    favs,
    mapName
  };
};

/**
 * @desc Función que enseña o oculta componentes del visor y actualiza el nombre
 */
export const getParametrizacion = async (id) => {
  let url = config.url_visorConf + id;

  const resp = await fetch(url);
  const data = await resp.json();

  const components = data.components;

  //Actualizamos el título de la pestaña
  document.title = data.titol;

  //Actualizamos la descripción de la página
  let metaTag = document.querySelector('meta[name="description"]');
  if (metaTag) {
    metaTag.setAttribute('content', data.descripcio);
  }

  //Actualizamos las variables de texto de la página como el nombre del visor y las URL
  Object.assign(visorVariables, {
    name: data.titol, // Nombre en la navbar
    correuSuggeriment: components.correu_suggeriment, // URL del correo de contacto
    urlManual: components.url_manual, // URL del manual a descargar
    descripcio: data.descripcio, // Descripción en la sidebar
    urlTutorial: components.url_videotutorials, //URL de los tutoriales
    urlFaqs: components.url_faqs //URL de las Faqs
  });

  //Actualizamos los componentes
  Object.assign(displayComponents, {
    //Navbar
    navbar: components.cBarra,
    cerca: components.cerca,
    imprimir: components.einesImpressio,
    compartir: components.linkMapa,
    accessibilitat: components.accessibilitat,
    contact: components.enviarSug,
    ajuda: components.cAjuda,
    fullScreen: components.pantallaCompleta,

    //Botons de mapa
    selectorFons: components.seleccioFons,
    llegenda: components.cLlegenda,
    cataleg: components.obrirCataleg,
    einesMesura: components.cEinesMesuraDibuix,
    ubicacio: components.ubicacioUsuari,

    //Ajuda
    tutorial: components.videotutorials,
    // faqs: components.faqs,
    manual: components.manualAjuda,

    //cerca
    cercaAvançada: components.cercaAvancada,
    cercaLayers: components.capesCerca,
    cercaFeatures: components.elementsCerca,
    cercaPlaces: components.localitzacionsCerca,
    infoCerca: components.infoCerca,

    //Gestió de mapes
    descarrega: components.cGestioMapes,
    meusMapes: components.meusMapes,
    mapesPredefinits: components.mapesPredefinits,
    fitxerLocal: components.fitxerLocal,

    //Eines de llegenda
    llegendaClear: components.nouMapa,
    llegendaExpand: components.expandirLlegenda,
    llegendaHide: components.mostrarLlegenda,

    //Eines de capa:
    hideCapa: components.mostrarCapes,
    taulaDadesCapa: components.obrirTaulaDades,
    llegendaFiltre: components.filtreCapa,
    eliminarCapa: components.eliminarCapa,
    metadadesCapa: components.consultarMeta,
    exportarCapa: components.opcExportacio,
    zoomCapa: components.zoomCapa,
    infoCapa: true,
    styleCapa: components.selectorEstil,
    statsCapa: components.calculEstadistic,
    transCapa: components.transCapa,

    //Catàleg
    catalegExterns: components.catalegExterns,
    catalegUsuari: components.catalegUsuari,
    catalegCorp: components.catalegCorp,

    //Info Punt
    infoPunt: components.cPanellInformacio,
    infoPuntInfo: components.infoPunt,
    infoPuntPDF: components.exportarInfoPunt,
    featureExport: components.exportarElement,
    descarregaInfoPunt: components.descarregaInfoPunt,

    //Redlining
    drawDibuix: components.einesredlining,
    drawPunt: components.punt,
    drawLinia: components.linia,
    drawArea: components.area,
    drawCercle: components.cercle,
    drawLliure: components.lliure,
    drawText: components.text,
    drawDonwload: components.descarregarDibuix,

    //Funcionalitats
    galeria: components.galeria,
    preferits: components.cPreferits,
    escala: components.escala,
    mousePos: components.escala,
    edicio: components.edicioElement
  });

  //Si hay mapa, pedimos el XML para conseguir sus propiedades
  if (data.mapa) {
    const xmlString = await getMapXML(data.mapa);
    return getMapProperties(xmlString, data.idCataleg);
  } else {
    return {
      idCat: data.idCataleg,
      capaFons: null,
      transparenciaFons: null,
      layerNames: null,
      bbox: null,
      redLining: null
    };
  }
};

export const getMapXML = async (id) => {
  let urlXML = config.url_getMap + id;
  let XML = await fetch(urlXML);
  return await XML.text();
};

export const getUserMapXML = async (id) => {
  let urlXML = config.url_getUserMap + id;
  let XML = await fetch(urlXML);
  return await XML.text();
};

export const loadVisorLayers = (layers, that) => {
  layers.sort((a, b) => {
    return b.indexCapa - a.indexCapa;
  });

  layers.reverse().forEach(async (layer) => {
    let sitepLayer = that.state.sitepLayerInfoMap.find((lay) => lay.name === layer.name);

    if (!sitepLayer) {
      for (let lay of that.state.externalDefaultLayers) {
        if (lay.getNumChilds() > 0) {
          let foundSubLayer = lay.getChildLayers().find((subLay) => subLay.name === layer.name);
          if (foundSubLayer) {
            sitepLayer = foundSubLayer;
            break;
          }
        }
        if (lay.name === layer.name) {
          sitepLayer = lay;
          break;
        }
      }
    }
    if (sitepLayer !== undefined) {
      let layerStyle;
      sitepLayer.getStyles()?.forEach((style) => {
        if (style.getName() === layer.style) {
          layerStyle = layer.style;
        }
      });
      await createLayer(sitepLayer, that.state.map, that.state.arrayLayers, false, layerStyle, undefined);
      that.setState({});
    } else {
      toast.error(getTranslatedKey('layerNotLoaded1') + ` ${layer.name} ` + getTranslatedKey('layerNotLoaded2'));
    }
  });

  layers.forEach((layer) => {
    let layerStyle = layer.style === 'undefined' ? undefined : layer.style;
    let sitepLayer = that.state.sitepLayerInfoMap.find((lay) => lay.name === layer.name);

    if (!sitepLayer) {
      for (let lay of that.state.externalDefaultLayers) {
        if (lay.getNumChilds() > 0) {
          let foundSubLayer = lay.getChildLayers().find((subLay) => subLay.name === layer.name);
          if (foundSubLayer) {
            sitepLayer = foundSubLayer;
            break;
          }
        }
        if (lay.name === layer.name) {
          sitepLayer = lay;
          break;
        }
      }
    }

    if (sitepLayer) {
      //Cambiamos el estilo de la capa
      sitepLayer.getStyles()?.forEach((style, idx) => {
        if (style.getName() === layerStyle && idx !== 0) {
          sitepLayer.setStyleSelected(idx);
        }
      });
      //Ocultamos las capas marcadas como hidden
      if (!layer.visible) {
        sitepLayer.setVisible(false);
        sitepLayer.getOLLayer().setVisible(false);
      }
      //Cambiamos la transparencia si no es 1
      if (layer.transparent !== 1) {
        sitepLayer.getOLLayer().setOpacity(layer.transparent);
      }
      //Quitamos la info de la capa si no está marcada como infoEnabled
      if (!layer.infoEnabled) {
        sitepLayer.setInfoEnabled(false);
      }
    }
  });
};

export const loadFavs = (favs, context) => {
  favs.forEach(async (fav) => {
    if (fav.capa === 'punt') {
      const coords = fav.fid.split(',').map((num) => parseFloat(num.trim()));
      context.addToFav(coords, 'coords');
    } else {
      const layerName = fav.capa;
      const featureId = fav.fid;
      const layer = context.state.sitepLayerInfoMap.find((lay) => lay.getName() === layerName);
      let name = layer.getTitle();
      let url = `/ows/wfs?service=wfs&version=2.0.0&request=GetFeature&outputFormat=application/json&srsName=${config.EPSG_mapa}&featureID=${featureId}`;
      let feature = await fetch(url)
        .then(async (resp) => {
          return await resp.json();
        })
        .then((data) => {
          let geoJSON = new GeoJSON().readFeatures(data);
          let feature = geoJSON[0];
          Object.entries(feature.values_).forEach(([key, value]) => {
            if (key === layer.Tooltip || key === layer.getExtension().ToolTip) {
              name = value;
            }
          });
          return feature;
        });
      context.addToFav(feature, 'feature', name, layer);
    }
  });
};

export const fetchPredefMaps = async () => {
  let url = config.url_getMapList;
  let response = await fetch(url);
  return await response.json();
};

export const fetchUserMaps = async () => {
  let url = config.url_userMaps;
  let response = await fetch(url);
  return await response.json();
};

export const getVisorId = async (name) => {
  try {
    let url = '/manteniment/visors.json';
    const response = await fetch(url);
    const data = await response.json();
    let id = data.find((v) => v.name === name).id;
    return id;
  } catch (error) {
    console.error('Error:', error);
    return null;
  }
};

export const getVisorNameFromUrl = (url) => {
  let regex = /\/visors\/([^/?]+)\.html/;
  let match = url.match(regex);
  return match ? match[1] : undefined;
};
